import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import './Marketing.css';

/**
 * /auth/callback
 *
 * GHL SSO: backend redirects here with ?token_hash=...&type=magiclink
 * We call verifyOtp({ token_hash }) to get the team member's own session.
 * Then clear any old owner session from localStorage before navigating.
 */
const AuthCallbackPage = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState('Signing you in…');
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        let authSub = null;

        const handleCallback = async () => {
            try {
                // ── PATH 1: GHL SSO token_hash ─────────────────────────────────
                const params = new URLSearchParams(window.location.search);
                const tokenHash = params.get('token_hash');
                const otpType = params.get('type');

                if (tokenHash && otpType) {
                    setStatus('Verifying your access…');
                    const { data, error: otpErr } = await supabase.auth.verifyOtp({
                        token_hash: tokenHash,
                        type: otpType,
                    });
                    if (otpErr) {
                        console.error('[AUTH_CALLBACK] verifyOtp error:', otpErr);
                        throw new Error('Login failed: ' + otpErr.message + '. Please click the GHL link again.');
                    }
                    if (!data?.session) {
                        throw new Error('No session returned. Please try the GHL link again.');
                    }
                    await handleWithSession(data.session);
                    return;
                }

                // ── PATH 1.5: PKCE Auth code exchange (Standard Magic Link / Signup) ─
                const code = params.get('code');
                if (code) {
                    setStatus('Completing sign-in…');
                    const { data, error: exchangeErr } = await supabase.auth.exchangeCodeForSession(code);
                    if (exchangeErr) {
                        console.error('[AUTH_CALLBACK] exchangeCodeForSession error:', exchangeErr);
                        throw new Error('Login failed: ' + exchangeErr.message + '. Please request a new magic link.');
                    }
                    if (!data?.session) {
                        throw new Error('No session returned. Please try logging in again.');
                    }
                    await handleWithSession(data.session);
                    return;
                }

                // ── PATH 2: Standard hash-based magic link ─────────────────────
                setStatus('Signing you in…');
                let resolved = false;

                const proceed = async (session) => {
                    if (!mounted || resolved) return;
                    resolved = true;
                    await handleWithSession(session);
                };

                const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
                    if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session) {
                        proceed(session);
                    }
                });
                authSub = subscription;

                const { data: { session }, error: sessionError } = await supabase.auth.getSession();
                if (sessionError) throw sessionError;
                if (session) { await proceed(session); return; }

                await new Promise((resolve) => {
                    const timeout = setTimeout(resolve, 8000);
                    const check = setInterval(() => {
                        if (resolved) { clearTimeout(timeout); clearInterval(check); resolve(); }
                    }, 100);
                });

                if (!resolved && mounted) {
                    throw new Error('Unable to establish session. The link may have expired.');
                }
            } catch (err) {
                console.error('[AUTH_CALLBACK]', err);
                if (mounted) setError(err.message || 'Sign-in failed.');
            } finally {
                if (authSub) authSub.unsubscribe();
            }
        };

        const handleWithSession = async (session) => {
            if (!mounted) return;
            setStatus('Checking workspace access…');

            const token = session.access_token;

            const response = await fetch('/api/auth/verify-org-access', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                credentials: 'include',
            });
            const data = await response.json();
            console.log('[AUTH_CALLBACK] verify-org-access response:', response.status, data);

            if (!response.ok || data.error === 'no_access') {
                const reason = data.message || data.error || `HTTP ${response.status}`;
                if (mounted) setError(`Access Denied: ${reason}\n\nIf you believe this is an error, ask your workspace owner to check Team Management and ensure your account is enabled.`);
                return;
            }

            const { orgs } = data;

            // ── Clear any stale owner session from localStorage ─────────────────
            // Without this, Dashboard reads the old owner JWT and shows owner data.
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.setItem('supabase_token', token);

            if (orgs.length === 1) {
                await fetch('/api/auth/set-active-org', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ orgId: orgs[0].id }),
                });
                localStorage.setItem('ra_org_role', orgs[0].role);
                localStorage.setItem('ra_active_org_id', orgs[0].id);
                if (mounted) navigate('/dashboard', { replace: true });
            } else {
                sessionStorage.setItem('ra-pending-orgs', JSON.stringify(orgs));
                sessionStorage.setItem('ra-pending-token', token);
                if (mounted) navigate('/workspace-select', { replace: true });
            }
        };

        handleCallback();
        return () => { mounted = false; if (authSub) authSub.unsubscribe(); };
    }, [navigate]);

    return (
        <div className="login-container">
            <div className="login-card" style={{ textAlign: 'center' }}>
                {!error ? (
                    <>
                        <div style={{
                            width: '48px', height: '48px',
                            border: '3px solid #64FFDA33', borderTopColor: '#64FFDA',
                            borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                            margin: '0 auto 1.5rem',
                        }} />
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        <p style={{ color: '#8892B0' }}>{status}</p>
                    </>
                ) : (
                    <>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
                        <h2 className="login-title" style={{ color: '#FF6464', fontSize: '1.25rem' }}>Access Denied</h2>
                        <p style={{ color: '#8892B0', marginBottom: '1.5rem', lineHeight: '1.6' }}>{error}</p>
                        <button
                            onClick={async () => { await supabase.auth.signOut(); navigate('/login', { replace: true }); }}
                            className="btn-primary" style={{ width: '100%' }}>Sign Out</button>
                    </>
                )}
            </div>
        </div>
    );
};

export default AuthCallbackPage;
